import { apiRequest } from './axiosConfig';
import {
  MetaAccount,
  MetaCampaignBundleDraft,
  MetaChangeRequest,
  MetaConnectionState,
  MetaDashboardData,
  MetaDatePreset,
  MetaOAuthCandidates,
  MetaObjectChangeDraft,
  MetaResumePreview,
} from '../../types/metaConnection';

// Backend wraps every payload as { success, data, message? }.
// apiRequest returns the raw body, so we unwrap `.data` here.
interface Envelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

class MetaConnectionService {
  /** Current Meta connection for the org (live-verified permissions). */
  async getConnection(): Promise<MetaConnectionState> {
    const res = await apiRequest.get<Envelope<MetaConnectionState>>('/meta/connection');
    return res.data;
  }

  /** Connect using a Business Manager System User Token. */
  async connect(accessToken: string): Promise<MetaConnectionState> {
    const res = await apiRequest.post<Envelope<MetaConnectionState>>('/meta/connect', {
      access_token: accessToken,
    });
    return res.data;
  }

  /**
   * Start the customer-self OAuth flow. Returns the facebook.com authorization
   * URL that requests ads_read + ads_management (never business_management).
   */
  async initiateOAuth(): Promise<string> {
    const res = await apiRequest.post<Envelope<{ authorize_url: string }>>(
      '/ad-accounts/oauth/initiate',
      { provider: 'meta' },
    );
    return res.data.authorize_url;
  }

  /** Ad accounts the authorizing user can manage, stashed by the callback. */
  async getOAuthCandidates(state: string): Promise<MetaOAuthCandidates> {
    const res = await apiRequest.get<Envelope<MetaOAuthCandidates>>(
      '/ad-accounts/oauth/candidates',
      { params: { state } },
    );
    return res.data;
  }

  /** Persist the ad accounts the user picked. */
  async confirmOAuthAccounts(state: string, accountIds: string[]): Promise<MetaAccount[]> {
    const res = await apiRequest.post<Envelope<{ ad_accounts: MetaAccount[] }>>(
      '/ad-accounts/oauth/confirm',
      { state, account_ids: accountIds },
    );
    return res.data.ad_accounts;
  }

  /** 1 件の広告アカウントだけ連携を解除する（他のアカウントは残す）。 */
  async disconnectAccount(adAccountId: number): Promise<void> {
    await apiRequest.delete<Envelope<unknown>>(`/ad-accounts/${adAccountId}`);
  }

  /** Soft-disconnect all Meta ad accounts for the org. */
  async disconnect(): Promise<void> {
    await apiRequest.delete<Envelope<unknown>>('/meta/connection');
  }

  /** Live campaigns + insights + KPIs + AI proposals from the Graph API. */
  async getDashboard(params: {
    datePreset: MetaDatePreset;
    adAccountId?: string;
    lang?: 'ja' | 'en';
  }): Promise<MetaDashboardData> {
    const res = await apiRequest.get<Envelope<MetaDashboardData>>('/meta/dashboard', {
      params: {
        date_preset: params.datePreset,
        lang: params.lang ?? 'ja',
        ...(params.adAccountId ? { ad_account_id: params.adAccountId } : {}),
      },
    });
    return res.data;
  }

  // ---- Graph writes (ads_management) ------------------------------------

  /**
   * Save a change proposal. Nothing reaches Meta at this point — the response
   * is what the confirmation screen renders.
   */
  async createChange(
    draft: (MetaCampaignBundleDraft | MetaObjectChangeDraft) & { idempotency_key: string },
  ): Promise<MetaChangeRequest> {
    const res = await apiRequest.post<Envelope<MetaChangeRequest>>(
      '/meta-ads/change-requests',
      draft,
    );
    return res.data;
  }

  /** Dry-run: re-checks the safety gate and the field rules, no Graph call. */
  async validateChange(publicId: string): Promise<{ valid: boolean; checks: string[] }> {
    const res = await apiRequest.post<Envelope<{ valid: boolean; checks: string[] }>>(
      `/meta-ads/change-requests/${publicId}/validate`,
    );
    return res.data;
  }

  /** Apply the approved change. Everything is created/updated as PAUSED. */
  async executeChange(publicId: string): Promise<MetaChangeRequest> {
    const res = await apiRequest.post<Envelope<MetaChangeRequest>>(
      `/meta-ads/change-requests/${publicId}/execute`,
    );
    return res.data;
  }

  async getResumePreview(publicId: string): Promise<MetaResumePreview> {
    const res = await apiRequest.get<Envelope<MetaResumePreview>>(
      `/meta-ads/change-requests/${publicId}/resume-preview`,
    );
    return res.data;
  }

  async resumeChange(publicId: string, revision: string): Promise<MetaChangeRequest> {
    const res = await apiRequest.post<Envelope<MetaChangeRequest>>(
      `/meta-ads/change-requests/${publicId}/resume`,
      { revision },
    );
    return res.data;
  }

  /** Audit log for the caller's organization (latest 50). */
  async getAudit(): Promise<MetaChangeRequest[]> {
    const res = await apiRequest.get<Envelope<MetaChangeRequest[]>>('/meta-ads/audit');
    return res.data;
  }
}

export default new MetaConnectionService();
